import { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { gql, useMutation } from "@apollo/client";
import { Backdrop, Card, CircularProgress, Fade, Icon, LinearProgress, Modal } from "@mui/material";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftTypography from "components/SoftTypography";
import { getUproColor, shadeColor } from "utils/colors";

const COMPLETAR_CAMBIO = gql`
  mutation completarCambioPassword($cambioToken: String!, $codigo: String!, $passwordPlain: String!) {
    completarCambioPassword(cambioToken: $cambioToken, codigo: $codigo, passwordPlain: $passwordPlain) {
      token
      user {
        id
        username
        nombre
        tipoUser
        tempPassword
        permisos {
          id
          nombre
          pantalla
        }
      }
    }
  }
`;

const REENVIAR_CODIGO = gql`
  mutation reenviarCodigoCambioPassword($cambioToken: String!) {
    reenviarCodigoCambioPassword(cambioToken: $cambioToken)
  }
`;

const LARGO_CODIGO = 6;
const ESPERA_REENVIO = 60;
const PASOS = ["Aviso", "Código", "Contraseña"];

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  maxHeight: "95%",
  width: { xs: "92%", sm: 460 },
  overflowY: "auto",
  p: 0,
  outline: "none",
};

// Los mismos requisitos que valida el servidor
const requisitos = (password) => [
  { label: "Al menos 8 caracteres", ok: password.length >= 8 },
  { label: "Al menos una letra", ok: /[A-Za-zÁÉÍÓÚÜÑáéíóúüñ]/.test(password) },
  { label: "Al menos un número", ok: /\d/.test(password) },
];

const FORTALEZAS = [
  { label: "Muy débil", color: "error" },
  { label: "Débil", color: "error" },
  { label: "Aceptable", color: "warning" },
  { label: "Buena", color: "info" },
  { label: "Muy buena", color: "success" },
];

const fortaleza = (password) => {
  let puntos = 0;
  if (password.length >= 8) puntos += 1;
  if (password.length >= 12) puntos += 1;
  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) puntos += 1;
  if (/\d/.test(password)) puntos += 1;
  if (/[^A-Za-z0-9]/.test(password)) puntos += 1;
  return Math.min(puntos, 4);
};

const mensajeError = (error) =>
  error?.graphQLErrors?.[0]?.message ||
  error?.networkError?.result?.errors?.[0]?.message ||
  "No pudimos completar la operación. Intentá nuevamente.";

function CodigoInput({ value, onChange, disabled }) {
  const refs = useRef([]);
  const digitos = value.padEnd(LARGO_CODIGO, " ").split("").slice(0, LARGO_CODIGO);

  const actualizar = (index, digito) => {
    const nuevos = [...digitos];
    nuevos[index] = digito || " ";
    onChange(nuevos.join("").replace(/\s+$/, ""));
  };

  const handleChange = (index) => (event) => {
    const ingresado = event.target.value.replace(/\D/g, "");
    const previo = digitos[index].trim();
    // Autocompletado del código (one-time-code) o varios dígitos de una vez: se reparten
    if (ingresado.length > 1 && !(previo && ingresado.length === 2)) {
      const completo = (value.slice(0, index) + ingresado).slice(0, LARGO_CODIGO);
      onChange(completo);
      refs.current[Math.min(completo.length, LARGO_CODIGO - 1)]?.focus();
      return;
    }
    const digito = ingresado.slice(-1);
    actualizar(index, digito);
    if (digito && index < LARGO_CODIGO - 1) refs.current[index + 1]?.focus();
  };

  const handleKeyDown = (index) => (event) => {
    if (event.key === "Backspace" && digitos[index] === " " && index > 0) {
      refs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (event) => {
    const pegado = event.clipboardData.getData("text").replace(/\D/g, "").slice(0, LARGO_CODIGO);
    if (!pegado) return;
    event.preventDefault();
    onChange(pegado);
    refs.current[Math.min(pegado.length, LARGO_CODIGO - 1)]?.focus();
  };

  return (
    <SoftBox display="flex" justifyContent="center" gap={1} onPaste={handlePaste}>
      {digitos.map((digito, index) => (
        <SoftBox key={index} width={{ xs: 42, sm: 48 }}>
          <SoftInput
            inputRef={(el) => {
              refs.current[index] = el;
            }}
            value={digito.trim()}
            onChange={handleChange(index)}
            onKeyDown={handleKeyDown(index)}
            disabled={disabled}
            size="large"
            inputProps={{
              inputMode: "numeric",
              autoComplete: index === 0 ? "one-time-code" : "off",
              "aria-label": `Dígito ${index + 1} del código`,
              style: { textAlign: "center", fontSize: "1.25rem", fontWeight: 700, height: "auto", lineHeight: 1.3 },
            }}
          />
        </SoftBox>
      ))}
    </SoftBox>
  );
}

CodigoInput.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

function Progreso({ paso }) {
  return (
    <SoftBox display="flex" gap={1} px={3} pt={2.5}>
      {PASOS.map((nombre, index) => (
        <SoftBox key={nombre} flex={1} textAlign="center">
          <SoftBox
            height={6}
            borderRadius="lg"
            sx={{
              backgroundColor: index <= paso ? getUproColor("uproGreen") : "#e9ecef",
              transition: "background-color 300ms ease",
            }}
          />
          <SoftTypography
            variant="caption"
            fontWeight={index === paso ? "bold" : "regular"}
            color={index <= paso ? "dark" : "secondary"}
          >
            {nombre}
          </SoftTypography>
        </SoftBox>
      ))}
    </SoftBox>
  );
}

Progreso.propTypes = { paso: PropTypes.number.isRequired };

export default function ModalActualizarPassword({ open, cambioToken, emailEnmascarado, onCancel, onSuccess }) {
  const [paso, setPaso] = useState(0);
  const [codigo, setCodigo] = useState("");
  const [password, setPassword] = useState("");
  const [confirmacion, setConfirmacion] = useState("");
  const [mostrar, setMostrar] = useState(false);
  const [error, setError] = useState(null);
  const [aviso, setAviso] = useState(null);
  const [esperaReenvio, setEsperaReenvio] = useState(ESPERA_REENVIO);

  const [completarCambio, { loading: guardando }] = useMutation(COMPLETAR_CAMBIO);
  const [reenviarCodigo, { loading: reenviando }] = useMutation(REENVIAR_CODIGO);

  useEffect(() => {
    if (!open) return;
    setPaso(0);
    setCodigo("");
    setPassword("");
    setConfirmacion("");
    setError(null);
    setAviso(null);
    setEsperaReenvio(ESPERA_REENVIO);
  }, [open, cambioToken]);

  useEffect(() => {
    if (!open || esperaReenvio <= 0) return undefined;
    const timer = setTimeout(() => setEsperaReenvio((segundos) => segundos - 1), 1000);
    return () => clearTimeout(timer);
  }, [open, esperaReenvio]);

  const cumplidos = requisitos(password);
  const passwordValida = cumplidos.every((requisito) => requisito.ok);
  const coinciden = password.length > 0 && password === confirmacion;
  const nivel = FORTALEZAS[fortaleza(password)];

  const handleReenviar = async () => {
    setError(null);
    setAviso(null);
    try {
      await reenviarCodigo({ variables: { cambioToken } });
      setCodigo("");
      setEsperaReenvio(ESPERA_REENVIO);
      setAviso("Te enviamos un código nuevo.");
    } catch (err) {
      setError(mensajeError(err));
    }
  };

  const handleGuardar = async () => {
    setError(null);
    try {
      const { data } = await completarCambio({
        variables: { cambioToken, codigo, passwordPlain: password },
      });
      setPaso(3);
      setTimeout(() => onSuccess(data.completarCambioPassword), 1200);
    } catch (err) {
      const mensaje = mensajeError(err);
      setError(mensaje);
      if (/código/i.test(mensaje)) setPaso(1);
    }
  };

  const iconoVisibilidad = (
    <Icon sx={{ cursor: "pointer" }} onClick={() => setMostrar((valor) => !valor)}>
      {mostrar ? "visibility_off" : "visibility"}
    </Icon>
  );

  return (
    <Modal
      open={open}
      onClose={(event, reason) => {
        // Es obligatorio: no se cierra tocando afuera
        if (reason !== "backdropClick") onCancel();
      }}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{ timeout: 400 }}
    >
      <Fade in={open}>
        <Card sx={style}>
          <SoftBox
            px={3}
            pt={3}
            pb={2.5}
            textAlign="center"
            sx={{
              background: `linear-gradient(135deg, ${getUproColor("uproPink")} 0%, ${shadeColor(getUproColor("uproPink"), -15)} 100%)`,
            }}
          >
            <SoftBox
              mx="auto"
              mb={1.5}
              width={64}
              height={64}
              borderRadius="50%"
              display="flex"
              alignItems="center"
              justifyContent="center"
              sx={{ backgroundColor: "rgba(255,255,255,0.18)", border: "2px solid rgba(255,255,255,0.5)" }}
            >
              <Icon sx={{ color: "#fff", fontSize: "34px !important" }}>
                {paso === 3 ? "verified_user" : "shield"}
              </Icon>
            </SoftBox>
            <SoftTypography variant="h5" fontWeight="bold" sx={{ color: "#fff" }}>
              {paso === 3 ? "¡Listo!" : "Actualizá tu contraseña"}
            </SoftTypography>
          </SoftBox>

          {paso < 3 && <Progreso paso={paso} />}

          <SoftBox px={3} pt={2} pb={3}>
            {paso === 0 && (
              <>
                <SoftTypography variant="body2" color="text" textAlign="center">
                  Por tu seguridad, te pedimos que actualices tu contraseña.
                </SoftTypography>
                <SoftBox
                  mt={2}
                  p={1.5}
                  borderRadius="lg"
                  display="flex"
                  alignItems="center"
                  gap={1.5}
                  sx={{ backgroundColor: "#f4f8fb" }}
                >
                  <Icon sx={{ color: getUproColor("uproBlue") }}>mark_email_read</Icon>
                  <SoftTypography variant="button" color="text" fontWeight="regular">
                    Te enviamos un código de verificación a <b>{emailEnmascarado}</b>.
                  </SoftTypography>
                </SoftBox>
                <SoftBox mt={3}>
                  <SoftButton color="primary" circular fullWidth onClick={() => setPaso(1)}>
                    Continuar
                  </SoftButton>
                </SoftBox>
              </>
            )}

            {paso === 1 && (
              <>
                <SoftTypography variant="body2" color="text" textAlign="center" mb={2}>
                  Ingresá el código de 6 dígitos que recibiste por correo.
                </SoftTypography>
                <CodigoInput value={codigo} onChange={setCodigo} />
                <SoftBox mt={2} textAlign="center">
                  {esperaReenvio > 0 || reenviando ? (
                    <SoftTypography variant="button" color="secondary" fontWeight="regular">
                      ¿No te llegó? Podés pedir otro código en {esperaReenvio}s
                    </SoftTypography>
                  ) : (
                    <SoftTypography
                      component="button"
                      type="button"
                      variant="button"
                      fontWeight="bold"
                      onClick={handleReenviar}
                      sx={{
                        color: getUproColor("uproBlue"),
                        background: "none",
                        border: 0,
                        cursor: "pointer",
                        textDecoration: "underline",
                      }}
                    >
                      Reenviar código
                    </SoftTypography>
                  )}
                </SoftBox>
                <SoftBox mt={2}>
                  <SoftButton
                    color="primary"
                    circular
                    fullWidth
                    disabled={codigo.length !== LARGO_CODIGO}
                    onClick={() => {
                      setError(null);
                      setPaso(2);
                    }}
                  >
                    Continuar
                  </SoftButton>
                </SoftBox>
              </>
            )}

            {paso === 2 && (
              <>
                <SoftBox mb={1.5}>
                  <SoftTypography component="label" variant="caption" fontWeight="bold">
                    Nueva contraseña
                  </SoftTypography>
                  <SoftInput
                    type={mostrar ? "text" : "password"}
                    placeholder="Nueva contraseña"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    icon={{ component: iconoVisibilidad, direction: "right" }}
                    inputProps={{ autoComplete: "new-password" }}
                  />
                </SoftBox>
                {password.length > 0 && (
                  <SoftBox mb={1.5}>
                    <LinearProgress
                      variant="determinate"
                      color={nivel.color}
                      value={(fortaleza(password) / 4) * 100}
                      sx={{ height: 6, borderRadius: 3, backgroundColor: "#e9ecef" }}
                    />
                    <SoftTypography variant="caption" color="text">
                      Seguridad: <b>{nivel.label}</b>
                    </SoftTypography>
                  </SoftBox>
                )}
                <SoftBox mb={1.5}>
                  {cumplidos.map((requisito) => (
                    <SoftBox key={requisito.label} display="flex" alignItems="center" gap={1}>
                      <Icon
                        fontSize="small"
                        sx={{ color: requisito.ok ? getUproColor("uproGreen") : getUproColor() }}
                      >
                        {requisito.ok ? "check_circle" : "radio_button_unchecked"}
                      </Icon>
                      <SoftTypography variant="caption" color={requisito.ok ? "dark" : "secondary"}>
                        {requisito.label}
                      </SoftTypography>
                    </SoftBox>
                  ))}
                </SoftBox>
                <SoftBox mb={1}>
                  <SoftTypography component="label" variant="caption" fontWeight="bold">
                    Repetir contraseña
                  </SoftTypography>
                  <SoftInput
                    type={mostrar ? "text" : "password"}
                    placeholder="Repetir contraseña"
                    value={confirmacion}
                    onChange={(event) => setConfirmacion(event.target.value)}
                    error={confirmacion.length > 0 && !coinciden}
                    inputProps={{ autoComplete: "new-password" }}
                  />
                  {confirmacion.length > 0 && !coinciden && (
                    <SoftTypography variant="caption" color="error">
                      Las contraseñas no coinciden
                    </SoftTypography>
                  )}
                </SoftBox>
                <SoftBox mt={2} display="flex" gap={1}>
                  <SoftButton color="secondary" variant="outlined" circular onClick={() => setPaso(1)}>
                    Volver
                  </SoftButton>
                  <SoftButton
                    color="primary"
                    circular
                    fullWidth
                    disabled={!passwordValida || !coinciden || guardando}
                    onClick={handleGuardar}
                  >
                    {guardando && (
                      <>
                        <CircularProgress color="inherit" size={18} />
                        &nbsp;&nbsp;
                      </>
                    )}
                    Guardar contraseña
                  </SoftButton>
                </SoftBox>
              </>
            )}

            {paso === 3 && (
              <SoftBox textAlign="center" py={1}>
                <SoftTypography variant="body2" color="text">
                  Tu contraseña se actualizó correctamente. Estamos ingresando…
                </SoftTypography>
                <SoftBox mt={2}>
                  <CircularProgress size={26} sx={{ color: getUproColor("uproGreen") }} />
                </SoftBox>
              </SoftBox>
            )}

            {(error || aviso) && paso < 3 && (
              <SoftBox
                mt={2}
                p={1.25}
                borderRadius="md"
                display="flex"
                alignItems="center"
                gap={1}
                sx={{ backgroundColor: error ? "#fdecea" : "#e8f6f4" }}
              >
                <Icon fontSize="small" sx={{ color: error ? getUproColor("error") : getUproColor("uproGreen") }}>
                  {error ? "error_outline" : "check_circle"}
                </Icon>
                <SoftTypography variant="caption" color={error ? "error" : "dark"}>
                  {error || aviso}
                </SoftTypography>
              </SoftBox>
            )}

            {paso < 3 && (
              <SoftBox mt={2} textAlign="center">
                <SoftTypography
                  variant="caption"
                  color="secondary"
                  sx={{ cursor: "pointer", textDecoration: "underline" }}
                  onClick={onCancel}
                >
                  Volver al inicio de sesión
                </SoftTypography>
              </SoftBox>
            )}
          </SoftBox>
        </Card>
      </Fade>
    </Modal>
  );
}

ModalActualizarPassword.propTypes = {
  open: PropTypes.bool.isRequired,
  cambioToken: PropTypes.string,
  emailEnmascarado: PropTypes.string,
  onCancel: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
};
